import React, { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import { Marker, Polygon, Polyline, InfoWindow } from '@react-google-maps/api';
//tabla y card components
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import { cardTitle } from 'assets/jss/material-dashboard-pro-react.js';
import LoadingSpinner from 'components/Tvendo/Common/LoadingSpinner';

import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItem from '@material-ui/core/ListItem';
import List from '@material-ui/core/List';

//iconos
import { GpsFixed, LocalShipping } from '@material-ui/icons';

//modal
import { WithApp } from 'contexts/appContext';

//servicios
import GridItem from 'components/Grid/GridItem';
import GoogleMapCustom from 'components/Tvendo/Common/GoogleMapCustom';
import CardContainer from 'components/Tvendo/Common/CardContainer';
import { ListItemText } from '@material-ui/core';
import GridContainer from 'components/Grid/GridContainer';
import { wsGetAll } from 'services/WService';
import { formatDate } from 'hooks/Pipe';
import { currency } from 'hooks/Pipe';

const useStyles = makeStyles((theme) => ({
  cardIconTitle: {
    ...cardTitle,
    marginTop: '15px',
    marginBottom: '0px'
  },
  pills: {
    color: 'white',
    padding: '5px 8px 5px 8px',
    borderRadius: '10px',
    cursor: 'pointer'
  },
  icon: {
    fontSize: '20px',
    cursor: 'pointer',
    marginLeft: '5px'
  },
  listItemWithColor: {
    '&:hover': {
      backgroundColor: (props) => `${props.itemColor}15 !important`,
    },
  },
}));

const mapHeight = '950px';
export default WithApp(function ProgAnalysis(props) {
  const classes = useStyles();
  const { analysisData } = props
  const [infoData, setInfoData] = useState({
    show: false,
    title: '',
    description: '',
  });

  const [mapCenter, setMapCenter] = useState({
    lat: -14.07543457346045,
    lng: -75.73423487506565,
  });
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [mapZoom] = useState(14);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  
  const [customerInfo, setCustomerInfo] = useState({
    show: false,
    data: null,
  });

  function buildKey(vehicleId, managerId, date) {
    return `${vehicleId}-${managerId}-${date}`;
  }

  function geometryToPaths(geometry) {
    if (!geometry || !geometry.value || !geometry.value.length) return [];

    const polygon = geometry.value[0];

    return polygon.map(([lng, lat]) => ({ lat, lng }));
  }

  const openRouteInfo = (route) => {
    setInfoData({
      show: true,
      lat: route.centroid.value[1],
      lng: route.centroid.value[0],
      title: route.code,
      description:
        `${route.description}\n` +
        `Monto Total: ${currency(route.total_amount)}\n` +
        `Total Órdenes: ${route.total_orders}\n` +
        `Total Clientes: ${route.total_customers}`,
    });
  };

  const dates = React.useMemo(() => {
    if (!analysisData || analysisData.length === 0) return [];

    const d = [...new Set(
      analysisData.map(cfg => cfg.program_date.split(" ")[0])
    )];

    return d.sort();
  }, [analysisData]);

  const routesByDate = React.useMemo(() => {
    if (!analysisData) return [];

    return analysisData.flatMap(cfg =>
      (cfg.detail || []).map(d => ({
        ...d.route,
        total_amount: d.total_amount,
        total_orders: d.total_order,
        total_customers: d.total_customer,
        program_date: cfg.program_date.split(" ")[0],
        vehicle_id: cfg.vehicle?.id,
        manager_name: cfg.manager?.name,
        vehicle_name: cfg.vehicle?.name,
        manager_id: cfg.manager?.id,
        customers: d.customers || [],
      }))
    );
  }, [analysisData]);

  function generateColor(index, total, alpha = 1) {
    const hue = (index * 360 / total) % 360;
    return `hsla(${hue}, 70%, 55%, ${alpha})`;
  }

  const routeColorMap = React.useMemo(() => {
    const map = {};
    const uniqueKeys = [];

    routesByDate.forEach(r => {
      const colorKey = `${r.vehicle_id}-${r.manager_id}-${r.program_date}`;
      if (!uniqueKeys.includes(colorKey)) {
        uniqueKeys.push(colorKey);
      }
    });

    uniqueKeys.forEach((key, index) => {
      map[key] = generateColor(index, uniqueKeys.length); // Color sólido
    });

    return map;
  }, [routesByDate]);

  const filteredRoutes = React.useMemo(() => {
    if (!selectedVehicles.length) return [];

    return routesByDate.filter(r =>
      selectedVehicles.some(s =>
        s.vehicle_id === r.vehicle_id &&
        s.manager_id === r.manager_id &&
        s.program_date === r.program_date
      )
    );
  }, [routesByDate, selectedVehicles]);

  const filteredCustomers = React.useMemo(() => {
    if (!filteredRoutes.length) return [];

    return filteredRoutes.flatMap(route => 
      (route.customers || []).map(customer => ({
        ...customer,
        route_code: route.code,
        route_color: routeColorMap[`${route.vehicle_id}-${route.manager_id}-${route.program_date}`] || "#14c8bc"
      }))
    );
  }, [filteredRoutes, routeColorMap]);

  useEffect(() => {
    setSelectedRouteId(null);
    setInfoData({ show: false });
    setCustomerInfo({ show: false, data: null });
  }, []);

  useEffect(() => {
    // Cuando cambian los filtros o se refresca analysisData → limpiar selección
    setSelectedVehicles([]);
  }, [analysisData]);


  return (
    <>
      {/* <Card> */}
        <CardBody>
          <GridContainer>
            <GridItem xs={8}>
              <GoogleMapCustom
                mapContainerStyle={{
                  width: '100%',
                  height: mapHeight,
                }}
                zoom={mapZoom}
                center={mapCenter}
              >
                {filteredRoutes.map((route) => {
                  const paths = geometryToPaths(route.geometry);
                  const colorKey = `${route.vehicle_id}-${route.manager_id}-${route.program_date}`;
                  const color = routeColorMap[colorKey] || "#14c8bc";
                  return (
                    <Polygon
                      key={`${route.id}-${route.program_date}`}
                      paths={paths}
                      options={{
                        fillColor: color,
                        fillOpacity: 0.45,
                        strokeWeight: 2,
                      }}
                      onDblClick={() => openRouteInfo(route)}
                    />
                  );
                })}

                {filteredCustomers
                  .filter(customer => customer.Latitude && customer.Longitude && customer.Latitude !== '0' && customer.Longitude !== '0')
                  .map((customer, index) => (
                    <Marker
                      key={`customer-${customer.base_business_partner_id}-${index}`}
                      position={{
                        lat: parseFloat(customer.Latitude),
                        lng: parseFloat(customer.Longitude),
                      }}
                      onClick={() => {
                        setCustomerInfo({
                          show: true,
                          data: customer,
                        });
                        setInfoData({ show: false });
                      }}
                    />
                  ))}

                {infoData.show && (
                  <InfoWindow
                    position={{ lat: infoData.lat, lng: infoData.lng }}
                    onCloseClick={() => setInfoData({ show: false, description: '', title: '' })}
                    options={{
                      pixelOffset: {
                        width: 0,
                        height: -25,
                      },
                    }}
                  >
                    <div>
                      <h4>{infoData.title}</h4>
                      <h5>{infoData.description}</h5>
                    </div>
                  </InfoWindow>
                )}

                {customerInfo.show && customerInfo.data && (
                  <InfoWindow
                    position={{
                      lat: parseFloat(customerInfo.data.Latitude),
                      lng: parseFloat(customerInfo.data.Longitude),
                    }}
                    onCloseClick={() => setCustomerInfo({ show: false, data: null })}
                    options={{
                      pixelOffset: {
                        width: 0,
                        height: -25,
                      },
                    }}
                  >
                    <div>
                      <h4>{customerInfo.data.CardName}</h4>
                      <p><strong>Código:</strong> {customerInfo.data.CardCode}</p>
                      <p><strong>Ruta:</strong> {customerInfo.data.route_code}</p>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMapCustom>

            </GridItem>
            <GridItem xs={4}>
              <CardContainer
                title="Vehiculos y Choferes"
                cardBodyStyle={{
                  overflow: 'auto',
                  maxHeight: '500px',
                }}
              >
                <List>
                  {analysisData.sort((a, b) => (a.manager?.name || '').localeCompare(b.manager?.name || ''))
                    .map((item) => {
                      const colorKey = buildKey(item.vehicle?.id, item.manager?.id, item.program_date.split(" ")[0]);
                      const itemColor = routeColorMap[colorKey] || "#14c8bc";
                      const isSelected = selectedVehicles.some(v =>
                        v.key === buildKey(item.vehicle?.id, item.manager?.id, item.program_date.split(" ")[0])
                      );
                      
                      return (
                        <ListItem 
                          button 
                          key={item.id}
                          selected={isSelected}
                          style={{
                            borderLeft: `4px solid ${itemColor}`,
                            backgroundColor: isSelected ? itemColor.replace('hsla', 'hsla').replace(', 1)', ', 0.2)') : 'transparent',
                          }}
                          onClick={() => {
                            const key = buildKey(
                              item.vehicle?.id, item.manager?.id, item.program_date.split(" ")[0]
                            );

                            setSelectedVehicles(prev => {
                              if (prev.some(v => v.key === key)) {
                                return prev.filter(v => v.key !== key);
                              }
                              return [
                                ...prev,
                                {
                                  key,
                                  vehicle_id: item.vehicle?.id, 
                                  manager_id: item.manager?.id, 
                                  program_date: item.program_date.split(" ")[0],
                                }
                              ];
                            });
                          }}
                        >
                          <ListItemIcon>
                            <LocalShipping style={{ color: itemColor }}/>
                          </ListItemIcon>
                          <ListItemText 
                            primary={`${item.vehicle?.name} — ${item.manager?.name}`} 
                            secondary={`${formatDate(item.program_date, 'F')} - ${currency(item.total_amount)} • Órdenes: ${item.total_orders} • Clientes: ${item.total_customers}`}
                          />
                        </ListItem>
                      );
                    })}
                </List>
              </CardContainer>
              <CardContainer
                title="Rutas"
                cardBodyStyle={{
                  overflow: 'auto',
                  maxHeight: '300px',
                }}
              >
                <List>
                  {dates.map(date => {
                    const routesOfThisDate = filteredRoutes.filter(r => r.program_date === date);
                    if (routesOfThisDate.length === 0) return null;
                    return (
                      <React.Fragment key={date}>
                        <ListItem sx={{ backgroundColor: '#f0f0f0' }}>
                          <ListItemText primary={date} />
                        </ListItem>
                        {routesOfThisDate
                          .sort((a, b) => a.description.localeCompare(b.description))
                          .map(route => {
                            const routeKey = `${date}-${route.id}-${route.vehicle_id}`;
                            return (
                              <ListItem
                                button
                                key={routeKey}
                                onClick={() => {
                                  setSelectedRouteId(routeKey);
                                  openRouteInfo(route);
                                }}
                                selected={selectedRouteId === routeKey}
                              >
                                <ListItemIcon>
                                  <GpsFixed />
                                </ListItemIcon>
                                <ListItemText
                                  primary={`${route.description.trim()} — ${route.vehicle_name ?? 'Veh ' + route.vehicle_id}`}
                                  secondary={`• Total: ${currency(route.total_amount)} • Órdenes: ${route.total_orders} • Clientes: ${route.total_customers}`}
                                />
                              </ListItem>
                            );
                          })}
                      </React.Fragment>
                    );
                  })}
                </List>
              </CardContainer>

            </GridItem>
          </GridContainer>
        </CardBody>
      {/* </Card> */}
    </>
  );
});
